import React, { useEffect, useState } from 'react';
import useGtm from 'Douleutaras/hooks/gtm';
import { useLocation, useNavigate } from 'react-router';
import { useGetParams } from 'Douleutaras/hooks/get-params';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { sellerProfilesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import { PageEventNames } from 'Douleutaras/views/pro-profile/components/settings/gtm';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { useFlowParam } from 'Douleutaras/hooks/jobs/jobFlow';

import { StartJobSection } from './StartJobSection';
import { BidPositiveCcfSection } from './BidPositiveCcfSection';
import { CtaRebookNotReviewed } from './CtaRebookNotReviewed';
import { CtaRebookReviewed } from './CtaRebookReviewed';

interface CtaFloatingSectionProps {
  isLoggedIn: boolean;
  username: string;
  onClick?(): void;
}

export const CtaFloatingSection: React.FC<CtaFloatingSectionProps> = props => {
  const { isLoggedIn, username, onClick } = props;
  const { jobId, bidId } = useGetParams();
  const { track } = useGtm();
  const navigate = useNavigate();
  const location = useLocation();
  const flow = useFlowParam();
  const autoDealId = location.state?.autoDealId;

  const { loading } = useAppSelector(state => state.sellerProfiles);
  const profile = useAppSelector(state => sellerProfilesSelectors.selectByUsername(state, username));

  // Render only after client mount to avoid SSR hydration mismatch.
  // This section depends on client-only data (URL params, auth state, job/bid)
  // that may differ between server and client.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  if (
    !mounted
    || loading
    || !profile
    || Boolean(profile.attributes.cleanerSettings)
  ) {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  const navigateToPostJob = (jobFlow: POST_JOB_FLOW = POST_JOB_FLOW.DIRECT_CONTACT) => {
    navigate({
        pathname: 'post-job',
        search: location.search
      },
      {
        state: {
          flow: jobFlow,
          autoDealId
        },
        replace: true
      }
    );
  };

  if (flow === POST_JOB_FLOW.SINGLE_BID) {
    return <StartJobSection
      name={profile.attributes.sellerDisplayName}
      flow={POST_JOB_FLOW.SINGLE_BID}
      onPrimaryClicked={() => {
        navigateToPostJob(POST_JOB_FLOW.SINGLE_BID);
      }}
    />;
  }

  if (flow === POST_JOB_FLOW.EXTRA_BID) {
    return <StartJobSection
      name={profile.attributes.sellerDisplayName}
      flow={POST_JOB_FLOW.EXTRA_BID}
      onPrimaryClicked={() => {
        typeof onClick === 'function' && onClick();
      }}
    />;
  }

  // When the customer is not logged in
  if (!isLoggedIn) {
    return (
      <StartJobSection name={profile.attributes.sellerDisplayName}
                       onPrimaryClicked={() => {
                         navigateToPostJob();
                         track(PageEventNames.CTA_CLICKED, undefined, 'Book');
                       }}
      />
    );
  }

  const showPositiveFlow = jobId !== undefined && bidId !== undefined && profile;

  // When a job is still active - show positive flow
  if (showPositiveFlow) {
    return (
      <BidPositiveCcfSection sellerId={profile.id}
                             bidId={bidId as string}
                             jobId={jobId as string}
                             onPrimaryClicked={() => {}}
      />
    );
  }

  const hasJobs = profile.attributes.concludedJobsCntForUser > 0;
  const needReview = Boolean(profile.attributes.lastUnreviewedBidForUser);

  return (
    <>
      {/* When a customer has no existing jobs with professional */}
      { !hasJobs &&
        <StartJobSection name={profile.attributes.sellerDisplayName}
                         onPrimaryClicked={() => {
                           navigateToPostJob();
                           track(PageEventNames.CTA_CLICKED, undefined, 'Start the job');
                         }}
        />
      }

      {/* When a customer has concluded jobs with professional but not provide a review */}
      { (hasJobs && needReview) &&
        <CtaRebookNotReviewed name={profile.attributes.sellerDisplayName}
                              jobId={profile.attributes.lastUnreviewedJobForUser}
                              bidId={profile.attributes.lastUnreviewedBidForUser}
                              onPrimaryClicked={() => {
                                navigateToPostJob();
                                track(PageEventNames.CTA_CLICKED, undefined, 'Rebook');
                              }}
        />
      }

      {/* When a customer has concluded jobs with professional and have provided a review */}
      { (hasJobs && !needReview) &&
        <CtaRebookReviewed name={profile.attributes.sellerDisplayName}
                           onPrimaryClicked={() => {
                             navigateToPostJob();
                             track(PageEventNames.CTA_CLICKED, undefined, 'Rebook');
                           }}
        />
      }
    </>
  );
};
